import React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import Tooltip from "components/Tooltip";
import { ProductUsageBuildingItemOUT } from "api-client";

interface UsageRow {
  name: string;
  match_count: number;
}

/**
 * Table columns for the product-usage drill-down. Components show name +
 * mappings; buildings additionally show the affected inventory entries.
 */
export default function useProductUsageColumns(isBuildings: boolean) {
  const { t } = useTranslation();

  return React.useMemo<ColumnDef<unknown, unknown>[]>(() => {
    const nameColumn: ColumnDef<unknown, unknown> = {
      accessorFn: (row) => (row as UsageRow).name,
      id: "name",
      cell: (info) => {
        const name = (info.row.original as UsageRow)?.name ?? "";
        return (
          <div className="whitespace-nowrap flex">
            <Tooltip truncate content={name}>
              {name}
            </Tooltip>
          </div>
        );
      },
      header: () => (
        <div>
          {isBuildings
            ? t("productEditor.productUsageBuildingColumn")
            : t("mapping.component")}
        </div>
      ),
      enableColumnFilter: false,
      enableSorting: false,
    };

    const matchColumn: ColumnDef<unknown, unknown> = {
      accessorFn: (row) => (row as UsageRow).match_count,
      id: "match_count",
      header: () => <div>{t("productEditor.numberOfMappings")}</div>,
      cell: (info) => (
        <div>{(info.row.original as UsageRow)?.match_count ?? ""}</div>
      ),
      enableColumnFilter: false,
      enableSorting: false,
    };

    if (!isBuildings) return [nameColumn, matchColumn];

    const calculationsColumn: ColumnDef<unknown, unknown> = {
      id: "affected_calculations",
      header: () => <div>{t("productEditor.numberOfEntries")}</div>,
      cell: (info) => (
        <div>
          {(info.row.original as ProductUsageBuildingItemOUT)
            ?.affected_calculations ?? ""}
        </div>
      ),
      enableColumnFilter: false,
      enableSorting: false,
    };

    return [nameColumn, matchColumn, calculationsColumn];
  }, [isBuildings, t]);
}
